import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Pencil, Trash2, Lock, Package, Tag,
  Wifi, Tv, Network, Cable, Shield, Cpu, Wrench, Boxes, Server, Layers } from "lucide-react";
import { toast } from "sonner";

// Known KaribuNet categories get a curated icon + gradient. Anything else
// (e.g. a category added later via Inventory > Product Categories) falls
// back to a generic style so the catalog never breaks visually.
const CATEGORY_STYLE = {
  "Internet Packages": { icon: Wifi, color: "from-indigo-500 to-blue-500" },
  "Internet Installation": { icon: Tv, color: "from-blue-500 to-cyan-500" },
  "CCTV Services": { icon: Shield, color: "from-emerald-500 to-teal-500" },
  "CCTV": { icon: Shield, color: "from-emerald-600 to-green-500" },
  "Networking Services": { icon: Network, color: "from-violet-500 to-purple-500" },
  "Networking Equipment": { icon: Server, color: "from-purple-500 to-fuchsia-500" },
  "Fiber Installation": { icon: Cable, color: "from-amber-500 to-orange-500" },
  "Fiber": { icon: Cable, color: "from-orange-500 to-amber-600" },
  "ICT Consultancy": { icon: Cpu, color: "from-rose-500 to-pink-500" },
  "Cables & Connectors": { icon: Cable, color: "from-cyan-500 to-sky-500" },
  "Conduit & Fittings": { icon: Wrench, color: "from-yellow-500 to-amber-600" },
  "Storage & Display": { icon: Boxes, color: "from-slate-500 to-zinc-500" },
  "Accessories": { icon: Layers, color: "from-teal-500 to-emerald-500" },
  "Services": { icon: Wrench, color: "from-rose-500 to-orange-500" },
  "Software": { icon: Cpu, color: "from-blue-600 to-indigo-600" },
};
const DEFAULT_STYLE = { icon: Package, color: "from-slate-400 to-slate-600" };

const fmt = (n) => new Intl.NumberFormat("en-TZ").format(n || 0);

export default function PriceList() {
  const { can } = useAuth();
  const canView = can("products", "view");
  const canCreate = can("products", "create");
  const canEdit = can("products", "edit");
  const canDelete = can("products", "delete");
  const canViewCategories = can("product_categories", "view");
  const canCreateCategory = can("product_categories", "create");
  const canEditCategory = can("product_categories", "edit");
  const canDeleteCategory = can("product_categories", "delete");
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({});
  const [savingCategory, setSavingCategory] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/products/", { params: { limit: 1000 } }),
        api.get("/product-categories/", { params: { limit: 200 } }),
      ]);
      setItems(p.data.items || []);
      setCategories((c.data.items || []).sort((a, b) => a.name.localeCompare(b.name)));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (canView) load(); else setLoading(false); }, [canView]);

  // Arriving from the header's global search: open that one product's edit
  // dialog once its data has loaded.
  useEffect(() => {
    const openId = location.state?.openId;
    if (!openId || loading) return;
    const match = items.find((it) => it.id === openId);
    if (match) openEdit(match);
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, loading]);

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.name, label: c.name })),
    [categories]
  );

  const openNew = (category) => { setEditing(null); setForm({ category: category || "" }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };

  const save = async () => {
    try {
      const payload = {
        name: form.name,
        category: form.category,
        recurring: form.recurring || "one-time",
        price: Number(form.price) || 0,
        // Missing on older products = TZS. Invoices convert at their own rate.
        currency: form.currency === "USD" ? "USD" : "TZS",
      };
      if (form.cost !== undefined && form.cost !== "") payload.cost = Number(form.cost);
      if (form.stock !== undefined && form.stock !== "") payload.stock = Number(form.stock);
      if (form.low_stock !== undefined && form.low_stock !== "") payload.low_stock = Number(form.low_stock);
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products/", payload);
      toast.success(editing ? "Updated" : "Added");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (it) => {
    if (!confirm(`Remove ${it.name}?`)) return;
    try { await api.delete(`/products/${it.id}`); load(); toast.success("Removed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Category management lives here now instead of a separate Inventory
  // page -- categories only ever exist to group products, so they're
  // edited right where products are.
  const openEditCategory = (cat) => { setEditingCategory(cat); setCategoryForm({ ...cat }); };

  const saveCategory = async () => {
    setSavingCategory(true);
    try {
      const payload = { name: categoryForm.name, code: categoryForm.code, description: categoryForm.description || "" };
      if (editingCategory) await api.put(`/product-categories/${editingCategory.id}`, payload);
      else await api.post("/product-categories/", payload);
      toast.success(editingCategory ? "Category updated" : "Category added");
      setEditingCategory(null);
      setCategoryForm({});
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingCategory(false); }
  };

  const delCategory = async (cat) => {
    const inUse = items.some((i) => i.category === cat.name);
    if (inUse) { toast.error("Move or remove this category's products first"); return; }
    if (!confirm(`Delete category "${cat.name}"?`)) return;
    try { await api.delete(`/product-categories/${cat.id}`); load(); toast.success("Category deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="price-list-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-amber-500 font-semibold">Catalog</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Products &amp; Price List</h1>
          <p className="text-sm text-muted-foreground mt-1">Every product and service KaribuNet sells, with current pricing.</p>
        </div>
        <div className="flex items-center gap-2">
          {canViewCategories && (
            <Button variant="outline" onClick={() => setCategoriesOpen(true)} data-testid="manage-categories-button">
              <Tag className="w-4 h-4 mr-2" /> Categories
            </Button>
          )}
          {canCreate ? (
            <Button onClick={() => openNew()} className="kn-gradient-bg text-white border-0" data-testid="price-list-add">
              <Plus className="w-4 h-4 mr-2" /> Add Item
            </Button>
          ) : <Button disabled variant="outline"><Lock className="w-3.5 h-3.5 mr-2" /> Read-only</Button>}
        </div>
      </div>

      {!canView ? (
        <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="module-forbidden">
          <Lock className="w-8 h-8 text-muted-foreground mb-3" />
          <p className="font-display text-lg font-medium">You don't have permission to view this module</p>
          <p className="text-sm text-muted-foreground mt-1">Ask an administrator to grant you access from Roles &amp; Permissions.</p>
        </div>
      ) : loading ? <Skeleton className="h-96 rounded-xl" /> : (
        <div className="space-y-8">
          {categories.map((cat) => {
            const style = CATEGORY_STYLE[cat.name] || DEFAULT_STYLE;
            const Icon = style.icon;
            const catItems = items.filter((i) => i.category === cat.name);
            return (
              <section key={cat.id}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${style.color} flex items-center justify-center shadow-md`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-semibold">{cat.name}</h3>
                      <p className="text-xs text-muted-foreground">{catItems.length} {catItems.length === 1 ? "item" : "items"}</p>
                    </div>
                  </div>
                  {canCreate && (
                    <Button variant="outline" size="sm" onClick={() => openNew(cat.name)}>
                      <Plus className="w-3.5 h-3.5 mr-1.5" /> Add
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {catItems.length === 0 ? (
                    <Card className="col-span-full p-6 text-sm text-muted-foreground border-dashed">No items in this category yet.</Card>
                  ) : catItems.map((it) => (
                    <Card key={it.id} className="kn-card p-4 rounded-xl border border-border bg-card">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{it.name}</p>
                          <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{it.sku}</p>
                          <p className="font-display text-2xl font-semibold mt-1 kn-gradient-text">
                            {it.currency === "USD" ? "USD" : "TZS"} {fmt(it.price)}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {it.recurring === "monthly" && (
                            <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-600 dark:text-amber-400">Monthly</Badge>
                          )}
                          {(it.stock !== undefined && it.stock !== null) && (
                            <Badge variant="outline" className="text-[10px]">Stock: {it.stock}</Badge>
                          )}
                        </div>
                      </div>
                      {(canEdit || canDelete) && (
                        <div className="flex justify-end gap-1 mt-3 pt-3 border-t border-border">
                          {canEdit && <Button size="icon" variant="ghost" onClick={() => openEdit(it)}><Pencil className="w-3.5 h-3.5" /></Button>}
                          {canDelete && <Button size="icon" variant="ghost" onClick={() => del(it)} className="hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Item" : "Add Product / Price List Item"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            {editing && (
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.16em]">SKU</Label>
                <Input value={form.sku || ""} disabled />
              </div>
            )}
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs uppercase tracking-[0.16em]">Category</Label>
              <SearchableSelect
                value={form.category || ""}
                onValueChange={(v) => setForm((s) => ({ ...s, category: v }))}
                options={categoryOptions}
                placeholder="Select category…"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs uppercase tracking-[0.16em]">Item / Product Name</Label>
              <Input value={form.name || ""} onChange={(e) => setForm(s => ({ ...s, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em]">Selling Price</Label>
              <div className="flex gap-2">
                <Select value={form.currency === "USD" ? "USD" : "TZS"} onValueChange={(v) => setForm(s => ({ ...s, currency: v }))}>
                  <SelectTrigger className="w-24 shrink-0" data-testid="product-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TZS">TZS</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
                <MoneyInput value={form.price ?? ""} onChange={(e) => setForm(s => ({ ...s, price: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em]">Cost Price (optional)</Label>
              <MoneyInput value={form.cost ?? ""} onChange={(e) => setForm(s => ({ ...s, cost: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em]">Stock (optional, physical items only)</Label>
              <Input type="number" value={form.stock ?? ""} onChange={(e) => setForm(s => ({ ...s, stock: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em]">Billing</Label>
              <Select value={form.recurring || "one-time"} onValueChange={(v) => setForm(s => ({ ...s, recurring: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one-time">One-time</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="kn-gradient-bg text-white border-0">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={categoriesOpen} onOpenChange={(o) => { setCategoriesOpen(o); if (!o) { setEditingCategory(null); setCategoryForm({}); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Product Categories</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-72">
            <div className="divide-y divide-border">
              {categories.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No categories yet</p>
              ) : categories.map((cat) => (
                <div key={cat.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm font-medium">{cat.name}</p>
                    <p className="text-[10px] font-mono text-muted-foreground">{cat.code}</p>
                  </div>
                  {(canEditCategory || canDeleteCategory) && (
                    <div className="flex gap-1">
                      {canEditCategory && (
                        <Button size="icon" variant="ghost" onClick={() => openEditCategory(cat)} data-testid={`edit-category-${cat.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {canDeleteCategory && (
                        <Button size="icon" variant="ghost" onClick={() => delCategory(cat)} className="hover:text-destructive" data-testid={`delete-category-${cat.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
          {(canCreateCategory || editingCategory) && (
            <div className="border-t border-border pt-3 space-y-2">
              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                {editingCategory ? `Editing "${editingCategory.name}"` : "Add a category"}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Category name" value={categoryForm.name || ""} onChange={(e) => setCategoryForm((s) => ({ ...s, name: e.target.value }))} data-testid="category-name-input" />
                <Input placeholder="SKU code (e.g. CCTV)" value={categoryForm.code || ""} onChange={(e) => setCategoryForm((s) => ({ ...s, code: e.target.value.toUpperCase() }))} data-testid="category-code-input" />
              </div>
              <div className="flex justify-end gap-2">
                {editingCategory && <Button variant="ghost" size="sm" onClick={() => { setEditingCategory(null); setCategoryForm({}); }}>Cancel</Button>}
                <Button size="sm" onClick={saveCategory} disabled={savingCategory || !categoryForm.name || !categoryForm.code} className="kn-gradient-bg text-white border-0" data-testid="save-category-button">
                  {savingCategory ? "Saving…" : editingCategory ? "Update" : "Add"}
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCategoriesOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
